// src/features/event/pages/EventAgreePage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { eventCard, EVENT_LOGO } from "../eventCard";
import { COLOR, FW } from "@/ui/tokens";

const TERMS_VERSION   = import.meta.env.VITE_TERMS_VERSION   ?? "2025-09-01";
const PRIVACY_VERSION = import.meta.env.VITE_PRIVACY_VERSION ?? "2025-09-01";

const SS_KEY = "bt.agree.checks";
function loadChecks() {
    try { return JSON.parse(sessionStorage.getItem(SS_KEY) || "{}"); } catch { return {}; }
}
function saveChecks(v: { terms: boolean; privacy: boolean; sms: boolean }) {
    sessionStorage.setItem(SS_KEY, JSON.stringify(v));
}

export default function EventAgreePage() {
    const nav = useNavigate();
    const location = useLocation();

    const saved = loadChecks();
    const [terms,   setTermsRaw]   = useState<boolean>(!!saved.terms);
    const [privacy, setPrivacyRaw] = useState<boolean>(!!saved.privacy);
    const [sms,     setSmsRaw]     = useState<boolean>(!!saved.sms);

    const setTerms   = (v: boolean) => { setTermsRaw(v);   saveChecks({ terms: v,     privacy, sms }); };
    const setPrivacy = (v: boolean) => { setPrivacyRaw(v); saveChecks({ terms, privacy: v,      sms }); };

    useEffect(() => {
        const agreed = (location.state as { agreed?: string } | null)?.agreed;
        if (agreed === "terms")   { setTermsRaw(true);   saveChecks({ terms: true,  privacy, sms }); }
        if (agreed === "privacy") { setPrivacyRaw(true); saveChecks({ terms, privacy: true,  sms }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state]);

    const allOn  = useMemo(() => terms && privacy && sms, [terms, privacy, sms]);
    const canNext = useMemo(() => terms && privacy, [terms, privacy]);

    const toggleAll = (next: boolean) => {
        setTermsRaw(next); setPrivacyRaw(next); setSmsRaw(next);
        saveChecks({ terms: next, privacy: next, sms: next });
    };

    const goNext = () => {
        const agreements = {
            terms_version: TERMS_VERSION,
            privacy_version: PRIVACY_VERSION,
            marketing_sms: sms,
        };
        sessionStorage.removeItem(SS_KEY);
        sessionStorage.setItem("bt.agreements", JSON.stringify(agreements));
        nav("/event/register", { state: { agreements }, replace: true });
    };

    return (
        <div style={eventCard.wrap}>
            <div style={eventCard.card}>
                {/* Header */}
                <div style={eventCard.header}>
                    <div style={eventCard.logoBox}>
                        <img src={EVENT_LOGO} alt="" style={{ width: 28, height: 28, filter: "invert(1)" }} />
                    </div>
                    <h1 style={eventCard.title}>이용 동의</h1>
                    <p style={eventCard.subtitle}>서비스 이용을 위해 아래 약관에 동의해주세요</p>
                </div>

                {/* Body */}
                <div style={eventCard.body}>
                    {/* 전체 동의 */}
                    <label style={s.checkRow}>
                        <input type="checkbox" checked={allOn} onChange={e => toggleAll(e.target.checked)} style={s.checkbox} />
                        <span style={{ ...s.checkLabel, fontWeight: FW.extraBold }}>전체 동의</span>
                    </label>

                    <div style={eventCard.divider} />

                    {/* 서비스 이용약관 */}
                    <div style={s.checkRowWrap}>
                        <label style={s.checkRow}>
                            <input type="checkbox" checked={terms} onChange={e => setTerms(e.target.checked)} style={s.checkbox} />
                            <span style={s.checkLabel}>[필수] 서비스 이용약관 동의</span>
                        </label>
                        <button onClick={() => nav("/event/legal/terms")} style={s.viewBtn}>전문보기</button>
                    </div>

                    {/* 개인정보처리방침 */}
                    <div style={s.checkRowWrap}>
                        <label style={s.checkRow}>
                            <input type="checkbox" checked={privacy} onChange={e => setPrivacy(e.target.checked)} style={s.checkbox} />
                            <span style={s.checkLabel}>[필수] 개인정보처리방침 동의</span>
                        </label>
                        <button onClick={() => nav("/event/legal/privacy")} style={s.viewBtn}>전문보기</button>
                    </div>


                    <button
                        onClick={goNext}
                        disabled={!canNext}
                        style={{ ...eventCard.ctaBtn, opacity: canNext ? 1 : 0.45, marginTop: 28 }}
                        onMouseEnter={e => { if (canNext) e.currentTarget.style.background = "#27272a"; }}
                        onMouseLeave={e => { if (canNext) e.currentTarget.style.background = "#18181b"; }}
                    >
                        다음
                    </button>
                </div>
            </div>
        </div>
    );
}

const s: Record<string, React.CSSProperties> = {
    checkRowWrap: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 0" },
    checkRow:     { display: "flex", alignItems: "center", gap: 10, cursor: "pointer", padding: "6px 0", flex: 1 },
    checkbox:     { width: 18, height: 18, cursor: "pointer", accentColor: COLOR.textDark, flexShrink: 0 },
    checkLabel:   { fontSize: 14, fontWeight: FW.medium, color: COLOR.textDark },
    viewBtn:      { flexShrink: 0, fontSize: 12, color: COLOR.textMuted, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", padding: 0 },
};